import { useEffect, useRef, useState } from 'react';
import { safeSetItem } from '../utils/safeLocalStorage';

/**
 * Multi-tab leader election via BroadcastChannel (localStorage fallback).
 *
 * First tab = leader (active).  Subsequent tabs see a blocking overlay
 * and can "claim" leadership, which demotes the current leader.
 *
 * The leader sends heartbeats every HEARTBEAT_MS.  If a tab misses
 * MAX_MISSED heartbeats it assumes the leader closed and self-promotes.
 */

const CHANNEL_NAME = 'bigwa-tab-leader';
const LS_KEY = 'bigwa-tab-leader';
const HEARTBEAT_MS = 2_000;
const LEADER_TIMEOUT_MS = HEARTBEAT_MS * 2.5; // 5 s

type Msg =
  | { type: 'heartbeat'; tabId: string }
  | { type: 'claim'; tabId: string }
  | { type: 'yield' };

const _win = window as unknown as Record<string, string>;

function tabId(): string {
  // Stable per page-load
  if (!_win.__bigwaTabId) {
    _win.__bigwaTabId =
      typeof crypto?.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
  return _win.__bigwaTabId;
}

// --------------- localStorage fallback helpers ---------------

function lsRead(): { tabId: string; ts: number } | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function lsWrite(id: string) {
  try {
    safeSetItem(LS_KEY, JSON.stringify({ tabId: id, ts: Date.now() }));
  } catch { /* quota / private mode */ }
}

function lsClear(id: string) {
  try {
    const current = lsRead();
    if (current?.tabId === id) localStorage.removeItem(LS_KEY);
  } catch { /* ignore */ }
}

// --------------- hook ---------------

export function useTabLeader() {
  const [isLeader, setIsLeader] = useState(false);
  const isLeaderRef = useRef(false);
  const id = useRef(tabId());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const heartbeatTimer = useRef<ReturnType<typeof setInterval>>();
  const lastHeartbeat = useRef(Date.now()); // eslint-disable-line react-hooks/purity
  const watchdogTimer = useRef<ReturnType<typeof setInterval>>();

  // Keep ref in sync so interval callbacks see latest value
  useEffect(() => { isLeaderRef.current = isLeader; }, [isLeader]);

  // ---- broadcast helpers ----
  const post = (msg: Msg) => {
    try { channelRef.current?.postMessage(msg); } catch { /* closed */ }
  };

  const becomeLeader = () => {
    setIsLeader(true);
    lsWrite(id.current);
  };

  const yieldLeadership = () => {
    setIsLeader(false);
    lsClear(id.current);
  };

  /** Called by the blocked tab's "Usar esta aba" button. */
  const claimLeadership = () => {
    post({ type: 'claim', tabId: id.current });
    // Also claim via localStorage for the fallback path
    lsWrite(id.current);
    becomeLeader();
  };

  useEffect(() => {
    let hasBroadcast = false;

    // ---------- Try BroadcastChannel ----------
    try {
      const ch = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = ch;
      hasBroadcast = true;

      ch.onmessage = (e: MessageEvent<Msg>) => {
        const msg = e.data;

        if (msg.type === 'heartbeat') {
          lastHeartbeat.current = Date.now();
          // Another tab is leader — if we thought we were, yield
          if (isLeaderRef.current && msg.tabId !== id.current) {
            yieldLeadership();
          }
        }

        if (msg.type === 'claim' && msg.tabId !== id.current) {
          // Another tab claimed leadership — yield
          yieldLeadership();
          post({ type: 'yield' });
        }
      };
    } catch {
      // BroadcastChannel unavailable — rely on localStorage
    }

    // ---------- Determine initial leadership ----------
    const existing = lsRead();
    const leaderAlive = existing && (Date.now() - existing.ts < LEADER_TIMEOUT_MS);

    if (!leaderAlive || existing?.tabId === id.current) {
      becomeLeader();
      // Announce immediately
      post({ type: 'heartbeat', tabId: id.current });
    } else {
      setIsLeader(false);
      lastHeartbeat.current = Date.now();
    }

    // ---------- Leader heartbeat ----------
    heartbeatTimer.current = setInterval(() => {
      if (isLeaderRef.current) {
        post({ type: 'heartbeat', tabId: id.current });
        lsWrite(id.current);
      }
    }, HEARTBEAT_MS);

    // ---------- Watchdog: detect dead leader ----------
    watchdogTimer.current = setInterval(() => {
      if (isLeaderRef.current) return; // we ARE the leader

      // Check localStorage timestamp (works even without BroadcastChannel)
      const ls = lsRead();
      const stale = !ls || (Date.now() - ls.ts > LEADER_TIMEOUT_MS);
      const bcStale = hasBroadcast && (Date.now() - lastHeartbeat.current > LEADER_TIMEOUT_MS);

      if (stale || bcStale) {
        becomeLeader();
        post({ type: 'heartbeat', tabId: id.current });
      }
    }, HEARTBEAT_MS);

    // ---------- Cleanup ----------
    const handleUnload = () => {
      lsClear(id.current);
      try { channelRef.current?.close(); } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', handleUnload);

    // localStorage "storage" event — cross-tab fallback
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== LS_KEY) return;
      if (!e.newValue) {
        // Leader cleared — try to claim
        if (!isLeaderRef.current) {
          // Small random delay to avoid thundering herd
          setTimeout(() => {
            const current = lsRead();
            if (!current) becomeLeader();
          }, Math.random() * 300);
        }
        return;
      }
      try {
        const data = JSON.parse(e.newValue);
        if (data.tabId !== id.current && isLeaderRef.current) {
          yieldLeadership();
        }
        lastHeartbeat.current = Date.now();
      } catch { /* malformed */ }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      clearInterval(heartbeatTimer.current);
      clearInterval(watchdogTimer.current);
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('storage', handleStorage);
      handleUnload();
    };
  }, []);

  return { isLeader, claimLeadership };
}
